import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone, Mail, Building, MessageSquare, Send, Loader2, Check,
  X, ExternalLink, Award, Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Type-appropriate CTA wording. Falls through to a sensible default so
// new marketplace types keep working.
function ctaConfigFor(listingType) {
  const lt = (listingType || "").toLowerCase();
  if (lt === "real-estate" || lt === "real_estate" || lt === "properties") {
    return {
      ownerLabel: "Listing Agent",
      ctaLabel: "Request a Tour",
      modalTitle: "Request a Tour",
      modalIntro: "Pick a time that works and the listing agent will reach out to confirm.",
      messagePlaceholder: "I'd love to schedule a tour. Best times for me are...",
      leadType: "Tour Request",
    };
  }
  if (lt === "vehicles") {
    return {
      ownerLabel: "Sales Consultant",
      ctaLabel: "Schedule a Test Drive",
      modalTitle: "Schedule a Test Drive",
      modalIntro: "Tell us when you'd like to come in and we'll have it ready for you.",
      messagePlaceholder: "I'd like to test drive this. Best times for me are...",
      leadType: "Test Drive Request",
    };
  }
  if (lt === "rvs") {
    return {
      ownerLabel: "RV Specialist",
      ctaLabel: "Schedule a Walkthrough",
      modalTitle: "Schedule an RV Walkthrough",
      modalIntro: "Let us know when you'd like a walkthrough — virtual or on-site.",
      messagePlaceholder: "I'd like a walkthrough of this RV. Best times for me are...",
      leadType: "RV Walkthrough Request",
    };
  }
  if (lt === "furniture") {
    return {
      ownerLabel: "Sales Associate",
      ctaLabel: "Ask About This Item",
      modalTitle: "Ask About This Item",
      modalIntro: "Have a question about dimensions, delivery, or availability? We'll get back to you.",
      messagePlaceholder: "I have a question about this item...",
      leadType: "Furniture Inquiry",
    };
  }
  return {
    ownerLabel: "Listing Owner",
    ctaLabel: "Request More Info",
    modalTitle: "Request More Info",
    modalIntro: "Tell us a bit about what you're looking for and we'll be in touch.",
    messagePlaceholder: "I'm interested in this listing...",
    leadType: "Marketplace Inquiry",
  };
}

function initialsOf(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

function LeadModal({ open, onClose, listing, config, accentColor }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.first_name || !form.email) {
      toast.error("Please enter your name and email so the agent can reach you.");
      return;
    }
    if (!listing.business_id) {
      toast.error("This listing isn't linked to a verified business yet. Try calling directly.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        business_id: listing.business_id,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        message: [
          `[${config.leadType}] ${listing.title || ""}`,
          listing.id ? `Listing ID: ${listing.id}` : "",
          form.message,
        ].filter(Boolean).join("\n"),
        service_interest: listing.title || "",
        lead_type: config.leadType,
        source: "marketplace_listing_detail",
      });
      setSuccess(true);
      toast.success("Sent! The agent will be in touch shortly.");
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setForm({ first_name: "", last_name: "", email: "", phone: "", message: "" });
      }, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="lead-modal-overlay"
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          className="bg-[#0F172A] border border-white/10 rounded-2xl w-full max-w-md p-6 relative"
          onClick={(e) => e.stopPropagation()}
          data-testid="lead-modal"
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white"
            data-testid="lead-modal-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {success ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="text-white text-lg font-bold mb-1">Request sent</h3>
              <p className="text-[#94A3B8] text-sm">The agent will follow up shortly.</p>
            </div>
          ) : (
            <>
              <h3 className="text-white text-lg font-bold mb-1" style={{ color: accentColor }}>{config.modalTitle}</h3>
              <p className="text-[#94A3B8] text-sm mb-5">{config.modalIntro}</p>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="First name *"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="lead-modal-first-name"
                  />
                  <Input
                    placeholder="Last name"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="lead-modal-last-name"
                  />
                </div>
                <Input
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="lead-modal-email"
                />
                <Input
                  type="tel"
                  placeholder="Phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="lead-modal-phone"
                />
                <textarea
                  rows={4}
                  placeholder={config.messagePlaceholder}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 text-sm resize-none"
                  data-testid="lead-modal-message"
                />
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 font-bold"
                  style={{ backgroundColor: accentColor }}
                  data-testid="lead-modal-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {config.ctaLabel}
                </Button>
              </form>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function ListingAgentCard({ listing, accentColor = "#7C3AED" }) {
  const [showModal, setShowModal] = useState(false);
  const cta = useMemo(() => ctaConfigFor(listing?.listing_type), [listing?.listing_type]);

  // Prefer the explicit owner record from the inventory item, then fall back
  // to the legacy seller_name / contact_* fields for older items
  const agentName = listing.listing_owner_name || listing.contact_name || listing.seller_name || "Listing Owner";
  const agentTitle = listing.listing_owner_title || cta.ownerLabel;
  const agentPhone = listing.listing_owner_phone || listing.contact_phone;
  const agentEmail = listing.listing_owner_email || listing.contact_email;

  const businessId = listing.business_id;
  const businessSlug = listing.business_slug;
  const businessName = listing.business_name;
  const businessLogo = listing.business_logo_url;

  const parentSlug = listing.parent_business_slug;
  const parentName = listing.parent_business_name;
  const parentLogo = listing.parent_business_logo_url;

  return (
    <div className="glass-card rounded-xl p-4 sm:p-6" data-testid="listing-agent-card">
      <h3 className="text-base sm:text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Shield className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: accentColor }} />
        Contact {agentTitle}
      </h3>

      {/* Agent block */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/10">
        <div
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${accentColor}, #00E5FF)` }}
          data-testid="listing-agent-avatar"
        >
          {initialsOf(agentName)}
        </div>
        <div className="min-w-0">
          <p className="text-white font-semibold text-sm sm:text-base truncate" data-testid="listing-agent-name">
            {agentName}
          </p>
          <p className="text-xs sm:text-sm text-[#94A3B8]" data-testid="listing-agent-title">
            {agentTitle}
          </p>
          {(listing.business_name || listing.parent_business_name) && (
            <p className="text-[11px] text-[#64748B] truncate">
              {listing.business_name}{parentName ? ` • ${parentName}` : ""}
            </p>
          )}
        </div>
      </div>

      {/* Brokerage / parent brokerage block */}
      {(businessId || businessSlug) && (
        <Link
          to={`/business/${businessSlug || businessId}`}
          className="flex items-center gap-3 p-3 mb-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-colors group"
          data-testid="listing-brokerage-link"
        >
          {businessLogo ? (
            <img
              src={businessLogo}
              alt={businessName}
              className="w-10 h-10 rounded-md object-cover bg-white flex-shrink-0"
              loading="lazy"
            />
          ) : (
            <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
              <Building className="w-5 h-5 text-white/60" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-white text-sm font-semibold truncate flex items-center gap-1">
              {businessName || "View Seller's Page"}
              <Award className="w-3 h-3 flex-shrink-0" style={{ color: accentColor }} />
            </p>
            <p className="text-[11px] text-[#94A3B8] flex items-center gap-1">
              View profile & all listings <ExternalLink className="w-3 h-3" />
            </p>
          </div>
        </Link>
      )}

      {parentSlug && parentName && parentSlug !== businessSlug && (
        <Link
          to={`/business/${parentSlug}`}
          className="flex items-center gap-3 p-3 mb-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-colors"
          data-testid="listing-parent-brokerage-link"
        >
          {parentLogo ? (
            <img src={parentLogo} alt={parentName} className="w-10 h-10 rounded-md object-cover bg-white flex-shrink-0" loading="lazy" />
          ) : (
            <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
              <Building className="w-5 h-5 text-white/60" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[11px] text-[#94A3B8]">Brokerage</p>
            <p className="text-white text-sm font-semibold truncate">{parentName}</p>
          </div>
        </Link>
      )}

      {/* Quick contact actions */}
      <div className="space-y-2">
        <Button
          type="button"
          onClick={() => setShowModal(true)}
          className="w-full h-11 text-sm font-bold"
          style={{ backgroundColor: accentColor }}
          data-testid="listing-cta-primary"
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          {cta.ctaLabel}
        </Button>

        {agentPhone && (
          <a href={`tel:${agentPhone}`} className="block" data-testid="listing-cta-call">
            <Button variant="outline" className="w-full h-10 text-sm border-white/20 text-white hover:bg-white/10">
              <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="truncate">{agentPhone}</span>
            </Button>
          </a>
        )}

        {agentEmail && (
          <a href={`mailto:${agentEmail}`} className="block" data-testid="listing-cta-email">
            <Button variant="outline" className="w-full h-10 text-sm border-white/20 text-white hover:bg-white/10">
              <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="truncate">Email {agentTitle}</span>
            </Button>
          </a>
        )}
      </div>

      <LeadModal
        open={showModal}
        onClose={() => setShowModal(false)}
        listing={listing}
        config={cta}
        accentColor={accentColor}
      />
    </div>
  );
}
